import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import i18n from '@/common/i18n/i18n.js';
import toastr from 'toastr';

import accountsRoutes from '@/app/accounts/routes.js';
import commonRoutes from '@/common/routes.js';
import datasetRoutes from '@/app/dataset/routes.js';
import indexerRoutes from '@/app/indexer/routes.js';
import jurisductionRoutes from '@/app/jurisdiction/routes.js';
import logsRoutes from '@/app/logs/routes.js';
import organisationRoutes from '@/app/organisation/routes.js';
import tasksRoutes from '@/app/tasks/routes.js';
import userRoutes from '@/app/user/routes.js';
import usergroupRoutes from '@/app/usergroup/routes.js';

Vue.use(VueRouter);

function normalizePathname(pathname) {
  const normalizedPath = decodeURI(pathname)
    // Replaces repeated slashes in the URL.
    .replace(/\/+/g, '/')
    // Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
    // Note: Missing native IE support, may want to skip this step.
    .normalize();
  return `${normalizedPath}/`;
}

const routes = [
  {
    path: '/:locale',
    component: () => import('@/common/views/Base.vue'),
    beforeEnter: (to, from, next) => {
    
      const locale = to.params.locale;
      const supported_locales = process.env.VUE_APP_I18N_SUPPORTED_LOCALE.split(',');
        
      if (!locale) {
        return next({
          path: `${process.env.VUE_APP_I18N_DEFAULT_LOCALE}${to.path.endsWith('/') ? to.path : normalizePathname(to.path)}`,
          query: to.query
        });
      }
    
      if (locale && locale.length !== 2) {
        return next({
          path: `${process.env.VUE_APP_I18N_DEFAULT_LOCALE}${to.path.endsWith('/') ? to.path : normalizePathname(to.path)}`,
          query: to.query
        });
      }
    
      if (locale && !supported_locales.includes(locale)) {
        return next({
          path:
            `${process.env.VUE_APP_I18N_DEFAULT_LOCALE}${to.path.slice(3).endsWith('/') ?
              to.path.slice(3) :
              normalizePathname(to.path.slice(3))}`,
          query: to.query
        });
      }

      if (!to.path.endsWith('/')) {
        return next({
          path: normalizePathname(to.path),
          query: to.query
        });
      }

      i18n.locale = locale;
    
      if (store.state.reloadIntervalId) {
        store.commit('CLEAR_RELOAD_INTERVAL_ID');
      }
      store.dispatch('login/GET_USER_AUTHENTICATION')
        .then(() => {
          const user = store.state['login'].userData;
    
          if (to.path.includes('account')) {
            if (user.is_superuser || (user.usergroup_roles && user.usergroup_roles.some(el => el.role === 2))) {
              return next();
            } else {
              return next({ name: '403Page' });
            }
          } else if (to.path.includes('data')) {
            if (user.is_superuser || (user.usergroup_roles && user.usergroup_roles.some(el => el.role >= 1))) {
              return next();
            } else {
              return next({ name: '403Page' });
            }
          } else {
            return next();
          }
        })
        .catch((err) => {
          console.error(err);
        });

      return next();
    
    },
    children: [
      ...commonRoutes,
      // Gestion des comptes
      ...accountsRoutes,
      // Utilisateurs
      ...userRoutes,
      // Organisations
      ...organisationRoutes,
      //Usergroups
      ...usergroupRoutes,
      // Indexers
      ...indexerRoutes,
      // Jurisdictions
      ...jurisductionRoutes,
      // Jeux de données
      ...datasetRoutes,
      // Logs
      ...logsRoutes,
      // Tasks
      ...tasksRoutes,
    ]
  },
  {
    path: '*',
    name: 'Fallback',
    beforeEnter: (to, from, next) => {
      const matchedRoutes = router.resolve(
        `${process.env.VUE_APP_I18N_DEFAULT_LOCALE}${to.path.endsWith('/') ?
          to.path :
          normalizePathname(to.path)}`
      ).resolved.matched;
      if (
        matchedRoutes && !matchedRoutes.find(el => el.name === 'Fallback')
      ) {
        return next({
          path: `${process.env.VUE_APP_I18N_DEFAULT_LOCALE}${to.path.endsWith('/') ? to.path : normalizePathname(to.path)}`,
          query: to.query
        });
      }
      return next({
        path: 'not-found/',
      });
    }
  },

];

const router = new VueRouter({
  routes,
  scrollBehavior: (to) => {
    if (to.hash) {
      return new Promise((resolve, reject) => {
        const startTime = Date.now();
        const maxWaitTime = 5000; // Délai d'attente maximal en millisecondes (par exemple, 5 secondes)
  
        const waitForElement = () => {
          const element = document.querySelector(to.hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            resolve();
          } else {
            const elapsedTime = Date.now() - startTime;
            if (elapsedTime >= maxWaitTime) {
              resolve({ x: 0, y: 0 }); // Retourner en haut si le délai est dépassé
            } else {
              setTimeout(waitForElement, 100); // Réessayer après 100 ms
            }
          }
        };
  
        waitForElement();
      });
    }
  },
  mode: 'history',
  base: process.env.VUE_APP_BASE_PATH || '',
});

router.beforeEach((to, from, next) => {
  // Clear toastr notifications
  toastr.clear();
  next();
});

export default router;
